import React from 'react';
import { Link } from 'react-router-dom';

const ModelSelection = () => {
  return (
    <>
      {/* Choosing the Right Model */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Choosing the Right Model</h2>
        <p className="text-gray-700 leading-relaxed mb-8">
          Selecting the optimal ASI1 model for your application involves balancing three key considerations: capabilities, speed, and cost. This guide helps you make an informed decision based on your specific requirements.
        </p>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
            <h3 className="text-lg font-semibold text-blue-700 mb-3">Capabilities</h3>
            <p className="text-gray-700">
              What specific features will you need the model to have to meet your Web3 and agentic AI requirements?
            </p>
          </div>
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
            <h3 className="text-lg font-semibold text-blue-700 mb-3">Speed</h3>
            <p className="text-gray-700">
              How quickly does the model need to respond in your application? Consider real-time vs. batch processing needs.
            </p>
          </div>
          <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
            <h3 className="text-lg font-semibold text-blue-700 mb-3">Cost</h3>
            <p className="text-gray-700">
              What's your budget for both development and production usage in your Web3 application?
            </p>
          </div>
        </div>
      </section>

      {/* Model Selection Matrix */}
      <section className="mb-16">
        <h2 className="text-3xl font-bold text-gray-800 mb-6">Model Selection Matrix</h2>
        <div className="bg-blue-50 p-6 rounded-lg border border-blue-100">
          <div className="overflow-x-auto">
            <table className="w-full border border-gray-300 rounded-lg">
              <thead className="bg-blue-100">
                <tr>
                  <th className="text-left p-4 border-b font-semibold">When you need...</th>
                  <th className="text-left p-4 border-b font-semibold">We recommend...</th>
                  <th className="text-left p-4 border-b font-semibold">Example use cases</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td className="p-4 border-b font-medium">Highest intelligence and reasoning for complex Web3 tasks</td>
                  <td className="p-4 border-b">
                    <Link to="/extended-model" className="text-blue-600 hover:text-blue-800 font-semibold">ASI1 Mini-Extended</Link>
                  </td>
                  <td className="p-4 border-b">Multi-agent frameworks, complex smart contract analysis, nuanced DeFi strategies, deep blockchain audits</td>
                </tr>
                <tr>
                  <td className="p-4 border-b font-medium">Ultra-fast responses for real-time applications</td>
                  <td className="p-4 border-b">
                    <Link to="/fast-model" className="text-blue-600 hover:text-blue-800 font-semibold">ASI1 Fast</Link>
                  </td>
                  <td className="p-4 border-b">High-frequency trading bots, instant agent discovery, real-time market analysis, millisecond-critical applications</td>
                </tr>
                <tr>
                  <td className="p-4 border-b font-medium">Balance of intelligence and speed for general workflows</td>
                  <td className="p-4 border-b">
                    <Link to="/mini-model" className="text-blue-600 hover:text-blue-800 font-semibold">ASI1 Mini</Link>
                  </td>
                  <td className="p-4 border-b">General Web3 applications, agentic workflows, smart contract interactions, decentralized app development</td>
                </tr>
                <tr>
                  <td className="p-4 border-b font-medium">Data visualization and analytics</td>
                  <td className="p-4 border-b">
                    <Link to="/graph-model" className="text-blue-600 hover:text-blue-800 font-semibold">ASI Graph</Link>
                  </td>
                  <td className="p-4 border-b">DeFi analytics dashboards, on-chain data visualization, market trend analysis, compliance reporting</td>
                </tr>
                <tr>
                  <td className="p-4 border-b font-medium">Natural conversational interfaces</td>
                  <td className="p-4 border-b">
                    <Link to="/voice-model" className="text-blue-600 hover:text-blue-800 font-semibold">ASI Voice</Link>
                  </td>
                  <td className="p-4 border-b">Voice-activated trading assistants, conversational Web3 guides, natural language blockchain queries</td>
                </tr>
                <tr>
                  <td className="p-4 font-medium">Specialized financial and DeFi expertise</td>
                  <td className="p-4">
                    <Link to="/finance-expert-agent" className="text-green-600 hover:text-green-800 font-semibold">ASI Finance Expert</Link>
                  </td>
                  <td className="p-4">Portfolio management, risk assessment, yield farming strategies, financial market analysis</td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </section>
    </>
  );
};

export default ModelSelection;
